<template>
  <GenericDialogLayout class="new-note-dialog"
                       :header-text="$t('client.notes.new_note_title')"
                       :is-submit-btn-disabled="!noteContent"
                       @close-dialog="handleClose"
                       @submit-dialog="handleSubmit(isEntity)"
                       :loading="submitting">

    <template slot="left-side-pane">
      <VcLoader v-if="loading"/>

      <div class="editor-wrapper"
           v-else>
        <Editor :init="tinyMceOptions"
                v-model="noteContent"
                @onKeyUp="onKeyUp" />
      </div>

      <div class="note-error"
           v-if="error">
        {{$t('common.errors.general')}}
      </div>
    </template>

  </GenericDialogLayout>
</template>

<script>
import {GenericDialogLayout} from "@/modules/common/components";
import {notesService} from "@/modules/common/services";

export default {
  name: "NewNoteDialog",
  components: {GenericDialogLayout},
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    isEntity: {
      type: Boolean,
      default: false
    },
    matterUid: {
      type: String,
      required: true
    },
    noteUid: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      noteContent: null,
      error: false,
      submitting: false,
      loading: false
    };
  },
  async created() {
    if (this.noteUid) {
      this.loading = true;

      try {
        const {data} = await notesService.get(this.matterUid, this.noteUid);
        this.noteContent = data.note.content;
      } catch {
        this.handleClose({isError: true});
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    tinyMceOptions() {
      const options = {
        skin: false,
        content_css: false,
        plugins: 'link paste code directionality lists',
        toolbar1: "alignleft aligncenter alignright alignjustify | ltr rtl | bullist numlist | link",
        toolbar2: "forecolor backcolor | undo redo | bold italic underline fontsize",
        fontsize_formats: "8px 10px 12px 14px 18px 24px 36px",
        forced_root_block : 'div',
        menubar: false,
        statusbar: false,
        default_link_target: "_blank",
        target_list: false,
        relative_urls : false,
        remove_script_host : false,
        inline: true,
        browser_spellcheck: true,
        contextmenu: false,
        placeholder: this.$t('client.notes.new_note_desc')
      };

      if (this.isMobile) {
        options.fixed_toolbar_container = '.new-note-dialog';
        options.setup = (editor) => {
          editor.on('click', () => {
            editor.focus();
          });
        }
      }

      return options;
    }
  },
  methods: {
    handleClose(data) {
      this.$emit('handle-close', data);
    },
    async handleSubmit(isEntity) {
      if (isEntity) {
        this.$emit('handle-submit', this.noteContent);
      } else {
        this.error = false;
        this.submitting = true;

        try {
          await notesService.add(this.matterUid, {content: this.noteContent});
          this.$emit('handle-submit');
        } catch {
          this.error = true;
        } finally {
          this.submitting = false;
        }
      }
    },
    onKeyUp() {
      const contentBody = document.querySelector('.mce-content-body');
      const noWhiteSpace = /\S/;
      if (!contentBody) return;
      if (!contentBody.innerText || !contentBody.innerText.match(noWhiteSpace)) this.noteContent = null;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.new-note-dialog {
  max-width: fit-content;

  @include md-and-up {
    height: calc(100vh - 20px);
    margin: 10px auto;
  }

  @include sm-and-down {
    height: 100vh;
  }

  ::v-deep .left-side-pane {
    flex-direction: column;
    padding: 30px;

    @include sm-and-down {
      width: 100vw;
    }

    .editor-wrapper {
      height: 100%;

      @include md-and-up {
        width: 616px;
      }

      .mce-content-body {
        height: calc(100vh - 215px);
        border: 1px solid $grey-color-2;
        padding: 15px;
        font-size: 13px;
        outline: unset;
        overflow: scroll;

        &[data-mce-placeholder]:not(.mce-visualblocks)::before {
          @include i-position-mirror(left, unset);
          color: $actionDisable;
        }

        &:focus-visible {
          outline: unset;
        }
      }
    }

    .v-progress-circular {
      width: 100% !important;

      @include md-and-up {
        width: 616px !important;
      }
    }

    .note-error {
      margin-top: 15px;
      color: $error_msg;
      font-size: 14px;
    }
  }

  ::v-deep .tox-tinymce-inline {
    @include sm-and-down {
      position: absolute !important;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/abstract/_mixins.scss";

.tox-tinymce-inline {
  z-index: 10;
}

.tox-notifications-container {
  display: none;
}
</style>
